import React, { useState } from 'react';
import {
    Box,
    Container,
    Typography,
    Grid,
    Card,
    CardContent,
    CardMedia,
    Dialog,
    DialogContent,
    IconButton,
    Paper,
    Tabs,
    Tab,
    CardActionArea
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { ChevronLeft, ChevronRight, X, MapPin, Calendar } from 'lucide-react';

// Styled Components
const EventCard = styled(Card)(({ theme }) => ({
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    transition: 'transform 0.2s ease-in-out',
    '&:hover': {
        transform: 'translateY(-8px)',
    },
}));

const ImageViewer = styled(Box)(({ theme }) => ({
    position: 'relative',
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
}));

const NavigationButton = styled(IconButton)(({ theme }) => ({
    position: 'absolute',
    backgroundColor: 'rgba(255, 255, 255, 0.8)',
    '&:hover': {
        backgroundColor: 'rgba(255, 255, 255, 0.9)',
    },
}));

const EventsPage = () => {
    const [selectedImage, setSelectedImage] = useState(null);
    const [currentPage, setCurrentPage] = useState(0);
    const [selectedTab, setSelectedTab] = useState(0);

    // Sample event data
    const events = [
        {
            id: 1,
            name: "IPR Importance & Support",
            place: "Seminar hall, Dr S & SS Ghandhi Government  Engineering College, Surat-395015",
            date: `Monday, 27 January 2025 from 3 pm to 6 pm `,
            description: "Empower Your Business Journey: NYCS Seminar on IPR Support and Opportunities for Aspiring Engineer Entrepreneurs.",
            brochurePages: [
                "/Events/gandhi-college-event/1.png",
                "/Events/gandhi-college-event/2.png",
                "/Events/gandhi-college-event/3.png",
            ],
            photos: [
                "/Events/img-1.jpg",
                "/Events/img-2.jpg",
                "/Events/img-3.jpg",
                "/Events/img-6.jpg"
            ]
        },
        {
            id: 2,
            name: "IPR Importance & Support",
            place: " Wisdom (Opposite Library), AB1, AURO UNIVERSITY, HAZIRA ROAD, SURAT-394510",
            date: `Monday, 27 January 2025 from 10 AM to 1 pm`,
            description: "Empower Your Business Journey: NYCS Seminar on IPR Support and Opportunities for Aspiring Engineer Entrepreneurs",
            brochurePages: [
                "/Events/Auro-university-event/1.png",
                "/Events/Auro-university-event/2.png",
                "/Events/Auro-university-event/3.png"
            ],
            photos: [
                "/Events/img-18.jpg",
                "/Events/img-19.jpg",
                "/Events/img-21.jpg"
            ]
        },
        {
            id: 3,
            name: " Work shop on Entrepreneurship and Self Employment ",
            place: "National Career Service Centre for SC/STs, ITI Campus, Majura Gate, Surat",
            date: "7th February, 2025 | Time: 12:00 PM",
            description: "Empowering Youth, Powering the Nation",
            brochurePages: [
                "/Events/img-7.jpg"
            ],
            photos: [
                "/Events/img-12.jpg",
                "/Events/img-13.jpg",
                "/Events/img-14.jpg",
                "/Events/img-15.jpg"
            ]
        }
    ];

    const handleImageOpen = (images, index = 0) => {
        setSelectedImage({ images, currentIndex: index });
        setCurrentPage(index);
    };

    const handleImageClose = () => {
        setSelectedImage(null);
        setCurrentPage(0);
    };

    const navigateImage = (direction) => {
        const newIndex = selectedImage.currentIndex + direction;
        if (newIndex >= 0 && newIndex < selectedImage.images.length) {
            setSelectedImage({
                ...selectedImage,
                currentIndex: newIndex
            });
        }
    };

    return (
        <Container maxWidth="lg" sx={{ py: 8 }}>
            {/* Page Title */}
            {/* <Typography variant="h3" component="h1" gutterBottom align="center" sx={{ mb: 6 }}>
                Our Events
            </Typography> */}
            <Typography
                variant="h3"
                gutterBottom
                sx={{
                    fontWeight: 700,
                    background: 'linear-gradient(to right, #1976d2, #512da8)',
                    backgroundClip: 'text',
                    textFillColor: 'transparent',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    mb: 3,
                    textAlign: "center"
                }}
            >
                Events
            </Typography>

            {/* Events Grid */}
            <Grid container spacing={4}>
                {events.map((event) => (
                    <Grid item xs={12} key={event.id} sx={{ mb: 6 }}>
                        <EventCard elevation={3}>
                            <CardContent>
                                <Typography variant="h4" gutterBottom>
                                    {event.name}
                                </Typography>

                                <Box sx={{ display: 'flex', gap: 2, mb: 2, color: 'text.secondary' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <MapPin size={20} />
                                        <Typography variant="subtitle1">
                                            {event.place}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Calendar size={20} />
                                        <Typography variant="subtitle1">
                                            {event.date}
                                        </Typography>
                                    </Box>
                                </Box>

                                <Typography variant="body1" paragraph>
                                    {event.description}
                                </Typography>

                                {/* Tabs for Brochure and Photos */}
                                <Box sx={{ width: '100%', mt: 3 }}>
                                    <Tabs
                                        value={selectedTab}
                                        onChange={(e, newValue) => setSelectedTab(newValue)}
                                        sx={{ mb: 3 }}
                                    >
                                        <Tab label="Event Brochure" />
                                        <Tab label="Event Photos" />
                                    </Tabs>

                                    {/* Brochure Preview */}
                                    <Box hidden={selectedTab !== 0}>
                                        <Grid container spacing={2}>
                                            {event.brochurePages.map((page, index) => (
                                                <Grid item xs={12} sm={6} md={4} key={index}>
                                                    <Card>
                                                        <CardActionArea onClick={() => handleImageOpen(event.brochurePages, index)}>
                                                            <CardMedia
                                                                component="img"
                                                                height="300"
                                                                image={page}
                                                                alt={`Brochure page ${index + 1}`}
                                                                sx={{ objectFit: 'cover' }}
                                                            />
                                                        </CardActionArea>
                                                    </Card>
                                                </Grid>
                                            ))}
                                        </Grid>
                                    </Box>

                                    {/* Photos Grid */}
                                    <Box hidden={selectedTab !== 1}>
                                        <Grid container spacing={2}>
                                            {event.photos.map((photo, index) => (
                                                <Grid item xs={12} sm={6} md={3} key={index}>
                                                    <Card>
                                                        <CardActionArea onClick={() => handleImageOpen(event.photos, index)}>
                                                            <CardMedia
                                                                component="img"
                                                                height="200"
                                                                image={photo}
                                                                alt={`Event photo ${index + 1}`}
                                                                sx={{ objectFit: 'cover' }}
                                                            />
                                                        </CardActionArea>
                                                    </Card>
                                                </Grid>
                                            ))}
                                        </Grid>
                                    </Box>
                                </Box>
                            </CardContent>
                        </EventCard>
                    </Grid>
                ))}
            </Grid>

            {/* Image Viewer Dialog */}
            <Dialog
                open={Boolean(selectedImage)}
                onClose={handleImageClose}
                maxWidth="lg"
                fullWidth
            >
                <DialogContent sx={{ position: 'relative', p: 0, height: '80vh' }}>
                    <IconButton
                        onClick={handleImageClose}
                        sx={{ position: 'absolute', top: 8, right: 8, zIndex: 1, bgcolor: 'rgba(255,255,255,0.8)' }}
                    >
                        <X />
                    </IconButton>

                    <ImageViewer>
                        {selectedImage && (
                            <>
                                <img
                                    src={selectedImage.images[selectedImage.currentIndex]}
                                    alt="Enlarged view"
                                    style={{ maxHeight: '100%', maxWidth: '100%', objectFit: 'contain' }}
                                />

                                {selectedImage.currentIndex > 0 && (
                                    <NavigationButton
                                        onClick={() => navigateImage(-1)}
                                        sx={{ left: 8 }}
                                    >
                                        <ChevronLeft />
                                    </NavigationButton>
                                )}

                                {selectedImage && selectedImage.currentIndex < selectedImage.images.length - 1 && (
                                    <NavigationButton
                                        onClick={() => navigateImage(1)}
                                        sx={{ right: 8 }}
                                    >
                                        <ChevronRight />
                                    </NavigationButton>
                                )}
                            </>
                        )}
                    </ImageViewer>
                </DialogContent>
            </Dialog>
        </Container>
    );
};

export default EventsPage;